import React, { useState, useEffect, useCallback } from 'react';
import { Shield, X } from 'lucide-react';
import { Button } from './ui/button';

const CONSENT_KEY = 'cookie_consent';
const CONSENT_VERSION = '1'; // bump to re-prompt users after policy changes

/**
 * Reads the stored consent. Returns null if no consent recorded,
 * or { analytics, advertising, version } object.
 */
function getStoredConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null; // re-prompt on version bump
    return parsed;
  } catch {
    return null;
  }
}

function storeConsent(analytics, advertising) {
  localStorage.setItem(
    CONSENT_KEY,
    JSON.stringify({ analytics, advertising, version: CONSENT_VERSION, timestamp: Date.now() })
  );
}

/**
 * Enable / disable PostHog based on consent.
 * PostHog is loaded in index.html but we can opt the user out.
 */
function applyAnalyticsConsent(allowed) {
  if (typeof window.posthog !== 'undefined') {
    if (allowed) {
      window.posthog.opt_in_capturing();
    } else {
      window.posthog.opt_out_capturing();
    }
  }
}

/**
 * Apply advertising consent (no-op, ads removed).
 */
function applyAdvertisingConsent(allowed) {
  // Ads removed — nothing to do
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [advertising, setAdvertising] = useState(true);

  // On mount, check for existing consent
  useEffect(() => {
    const consent = getStoredConsent();
    if (consent) {
      // Apply stored preferences silently
      applyAnalyticsConsent(consent.analytics);
      applyAdvertisingConsent(consent.advertising);
    } else {
      // No consent yet — show banner
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    storeConsent(true, true);
    applyAnalyticsConsent(true);
    applyAdvertisingConsent(true);
    setVisible(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    storeConsent(false, false);
    applyAnalyticsConsent(false);
    applyAdvertisingConsent(false);
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    storeConsent(analytics, advertising);
    applyAnalyticsConsent(analytics);
    applyAdvertisingConsent(advertising);
    setVisible(false);
  }, [analytics, advertising]);

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[10000] p-4 sm:p-6"
      style={{ pointerEvents: 'none', paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
    >
      <div
        className="max-w-2xl mx-auto rounded-2xl border border-[var(--md3-outline-variant)] shadow-2xl p-5 sm:p-6"
        style={{
          pointerEvents: 'auto',
          backgroundColor: 'var(--color-surface, #1a1d21)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Shield size={20} className="text-[var(--md3-primary)] flex-shrink-0" />
            <h3 className="text-base font-semibold text-[var(--color-on-surface,#fff)]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Cookie Preferences
            </h3>
          </div>
          <button
            onClick={handleRejectAll}
            className="text-[var(--color-on-surface-variant,#aaa)] hover:text-[var(--color-on-surface,#fff)] transition-colors"
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-[var(--color-on-surface-variant,#aaa)] mb-4 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
          We use cookies for analytics. Essential cookies (authentication, preferences) are always active.
          {' '}
          <a href="/privacy" className="underline text-[var(--md3-primary)] hover:opacity-80">Privacy Policy</a>
        </p>

        {/* Expandable details */}
        {showDetails && (
          <div className="mb-4 space-y-3 border-t border-[var(--md3-outline-variant)] pt-4">
            {/* Essential — always on */}
            <label className="flex items-center justify-between">
              <div>
                <span className="text-sm font-medium text-[var(--color-on-surface,#fff)]">Essential</span>
                <p className="text-xs text-[var(--color-on-surface-variant,#aaa)]">Authentication, security, preferences</p>
              </div>
              <span className="text-xs text-[var(--color-on-surface-variant,#aaa)] bg-[var(--md3-surface-container,#2a2d31)] px-2 py-1 rounded-full">Always on</span>
            </label>

            {/* Analytics */}
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-[var(--color-on-surface,#fff)]">Analytics</span>
                <p className="text-xs text-[var(--color-on-surface-variant,#aaa)]">Usage data to improve the app (PostHog)</p>
              </div>
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="w-5 h-5 rounded accent-[var(--md3-primary)]"
              />
            </label>
          </div>
        )}

        {/* Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="cookie-accept-all-btn"
            onClick={handleAcceptAll}
            className="flex-1 sm:flex-none bg-[var(--md3-primary)] hover:opacity-90 text-white rounded-full px-6 py-2 text-sm font-medium"
          >
            Accept All
          </Button>
          <Button
            data-testid="cookie-reject-all-btn"
            onClick={handleRejectAll}
            variant="outline"
            className="flex-1 sm:flex-none rounded-full px-6 py-2 text-sm font-medium border-[var(--md3-outline-variant)] text-[var(--color-on-surface,#fff)]"
          >
            Reject All
          </Button>
          {!showDetails ? (
            <button
              data-testid="cookie-customize-btn"
              onClick={() => setShowDetails(true)}
              className="text-sm text-[var(--md3-primary)] hover:underline ml-auto"
            >
              Customize
            </button>
          ) : (
            <Button
              data-testid="cookie-save-preferences-btn"
              onClick={handleSavePreferences}
              variant="outline"
              className="flex-1 sm:flex-none rounded-full px-6 py-2 text-sm font-medium border-[var(--md3-primary)] text-[var(--md3-primary)]"
            >
              Save Preferences
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
